import { size } from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
} from "react";

import { MusicTabData, MusicTabOptions } from "@/data/types";

interface AudioTabContextType {
  options: MusicTabOptions;
  data: MusicTabData[];
  canMoveUp(trackIndex: number): boolean;
  canMoveDown(trackIndex: number): boolean;
}

const AudioTabContext = createContext<AudioTabContextType | null>(null);

type Props = PropsWithChildren<{
  options: MusicTabOptions;
  data: MusicTabData[];
}>;

export const AudioTabProvider: FC<Props> = ({ children, options, data }) => {
  const canMoveUp = useCallback((trackIndex: number) => {
    const isFirstTrack = trackIndex === 0;

    return !isFirstTrack;
  }, []);

  const canMoveDown = useCallback(
    (trackIndex: number) => {
      const isLastTrack = trackIndex === size(data) - 1;

      return !isLastTrack;
    },
    [data],
  );

  return (
    <AudioTabContext.Provider value={{ options, data, canMoveUp, canMoveDown }}>
      {children}
    </AudioTabContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAudioTabContext = () => {
  const context = useContext(AudioTabContext);

  if (!context) {
    throw new Error(
      "useAudioTabContext must be used within an AudioTabProvider",
    );
  }

  return context;
};
